import { ChangeEvent, FormEvent, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { usePageLoadTracking } from '@/hooks/pageLoadTracking/usePageLoadTracking'

import { debounce } from '@/utils/functionalUtils'

import { ButtonSpinner, PrimaryButton } from '@/components/buttons/primaryButton'
import { PageTitle } from '@/components/pageTitle'
import { FooterPolicy } from '@/components/policyLinkBlock'
import { TextBlock } from '@/components/textBlock'
import { TextHTML } from '@/components/typography'

import { useAuth } from '@/auth/useAuth'
import { useSession } from '@/core/session/useSession'
import { trackPageComplete } from '@/events/events'

import { InputPageProps } from './types'

export function InputPage({
  pageName,
  pageConfig,
  setLastVisitedItem,
  setItem,
  config,
}: InputPageProps) {
  const { user } = useAuth()
  const sessionContext = useSession()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(false)
  const [value, setValue] = useState<string>('')

  const {
    title,
    skipText,
    nextUrl,
    pageDescription,
    unit,
    textBlocks,
    tabTitle,
    buttonName,
    checkInputValue,
    inputPlaceholder,
    isEndangered,
    policyBlock = false,
    onSubmit,
  } = pageConfig

  const navigate = useNavigate()

  usePageLoadTracking({ pageName, config, user, title: tabTitle || title })

  const nextPage = async (e?: FormEvent) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }

    trackPageComplete(
      pageName,
      [value],
      config.PARTNER_ID,
      config.APP_ID,
      config.PAGE_SET_ID,
      sessionContext,
      user
    )
    setItem(pageName, value)
    setIsLoading(true)
    try {
      if (onSubmit) {
        onSubmit(value)
      }
    } catch (e) {
      console.error(e)
    }

    await debounce(() => {
      navigate(nextUrl)
      setLastVisitedItem(pageName)
    }, 300)()
  }

  const skipQuestion = () => {
    nextPage()
  }

  const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setValue(event.currentTarget.value)
    if (!checkInputValue(event.currentTarget.value)) {
      setError(true)
    } else {
      setError(false)
    }
  }

  const className = `page page-survey page-input page-${pageName}`
  return (
    <div className={className}>
      <PageTitle title={title} />

      {pageDescription && <TextHTML className="page-description" title={pageDescription} />}

      <form className="input-container" onSubmit={nextPage}>
        <div className="input-wrapper">
          <input
            value={value}
            onChange={onInputChange}
            placeholder={inputPlaceholder || undefined}
          />
          {unit && <span className="unit">{' ' + unit}</span>}
        </div>
      </form>

      {textBlocks &&
        (isEndangered ? value : true) &&
        textBlocks.map(({ text, ...blockProps }) => {
          text = typeof text === 'function' ? text() : text
          return <TextBlock key={text} text={text} {...blockProps} />
        })}

      <div className="button-container float">
        <PrimaryButton disabled={error || !value || isLoading} onClick={nextPage}>
          {isLoading ? <ButtonSpinner /> : buttonName || 'Continue'}
        </PrimaryButton>
        {skipText && (
          <button className="skip-button" onClick={skipQuestion}>
            {skipText}
          </button>
        )}
      </div>
      {policyBlock && <FooterPolicy />}
    </div>
  )
}
